/**
 * CSV bulk-import validation for lexicon entries (VE-24948 / U5, U5-FR10-FR13).
 *
 * WHOLE-FILE, PRE-ACCEPTANCE. U5-FR12 forbids completing an upload while any
 * validation error remains, so there is no partial-import path at all: either
 * every row is accepted or none is. That is also why a successful import is a
 * single atomic write — see `models.ts` on storage granularity.
 *
 * DUPLICATE SCOPE IS ASYMMETRIC, AND THAT IS DELIBERATE (BR-22 / BR-23):
 *   - the same term twice WITHIN the file  -> blocking error ("Duplicate values")
 *   - a term already in the lexicon        -> non-blocking skip, reported as a count
 * Blocking the second case would turn re-uploading a corrected superset into an
 * error wall, which fights U5-FR13's whole purpose ("a further CSV ADDS terms").
 *
 * PURITY. `validateLexiconCsvText` is a pure function of (text, context) so the
 * entire error matrix is table-testable with no File, no store and no network.
 * `validateLexiconCsv` is the thin File wrapper that applies the size gate.
 *
 * THREE COLUMNS NAME THE SAME THING, AND THE UNQUALIFIED ONE IS THE DEFAULT.
 * `redaction_code` is matched case-insensitively against a code's `code` — the
 * `B6` stamped on the redaction, which is what an author has in front of them
 * and what the rest of the app means by "Redaction Code".
 * `redaction_code_name` (the `codeName`) and `redaction_code_id` are
 * alternatives, and each stands on its own — a file carrying only one of them
 * resolves from it. When more than one is filled in the most HUMAN wins:
 * `redaction_code`, then the name, then the id. See `validateRow` on why the id
 * is last rather than first, and `LexiconCsvContext.redactionCodes` for why
 * anything but the id is matched at all. `HEADER_ALIASES` below lists the other
 * spellings a header may use.
 *
 * The first three operator-facing strings below are fixed VERBATIM by U5-FR11.
 * They are acceptance criteria, not copy suggestions — translate them, but do
 * not reword the English. `csvMessageAmbiguousCode` is ours and carries no such
 * constraint.
 */

import Papa from 'papaparse';

import {
  CSV_MAX_BYTES,
  ENTRY_TERM_MAX_LENGTH,
  LEXICON_MAX_ENTRIES,
} from './limits';
import {
  LEXICON_CSV_COLUMNS,
  LexiconCsvCellError,
  LexiconCsvColumn,
  LexiconCsvRedactionCode,
  LexiconCsvValidationResult,
  LexiconEntryInput,
  LexiconEntryType,
} from './models';
import { unguardCsvCell } from './csvFormulaGuard';
import { validateRegexEntry } from './regexScreen';

/** Verbatim per U5-FR11. Do not reword. */
export const CSV_MESSAGE_INVALID_REGEX =
  'Invalid regex, please update and try your upload again';
/** Verbatim per U5-FR11. Do not reword. */
export const CSV_MESSAGE_DUPLICATE = 'Duplicate values';
/** Verbatim per U5-FR11. Do not reword. */
export const CSV_MESSAGE_UNKNOWN_CODE =
  'Redaction code not available, please add code in Redaction Code page';

/**
 * A cell that folds onto more than one live code.
 *
 * Reachable because the Redaction Codes page enforces uniqueness with an exact
 * server-side filter, so `B6` and `b6` — or `Personal Information` and
 * `personal information` — can both exist, and these columns fold case, which
 * is the whole reason they are typable. Two codes, one spelling in the file, no
 * way to pick: say so rather than guessing, and name the escape hatch.
 */
export function csvMessageAmbiguousCode(value: string): string {
  return (
    `More than one redaction code matches "${value}", differing only in ` +
    'capitalization. Clear this cell and use the redaction_code_id column ' +
    'instead.'
  );
}

export interface LexiconCsvContext {
  /**
   * Duplicate keys of the entries already in the target lexicon, from
   * `lexiconEntryDuplicateKey`. Used for the non-blocking skip (BR-23).
   */
  readonly existingKeys: ReadonlySet<string>;
  /** How many more entries the lexicon can hold before hitting BR-16. */
  readonly remainingHeadroom: number;
  /**
   * Every LIVE redaction code in this organization — see
   * `LexiconCsvRedactionCode` on why soft-deleted ones are excluded.
   *
   * THE RECORDS, NOT A SET OF IDS, which reverses an earlier decision. The id
   * was once the only thing a file could say, on the argument that a code's
   * text is a label an agency can rename and that two codes might carry the
   * same text — so a file naming codes said something the lexicon could not
   * resolve back to one row. True, but the
   * price was that the only usable value was one nobody can type from memory:
   * an author had to export a lexicon purely to copy ids out of it, which is a
   * strange thing to ask of someone writing a term list in Excel.
   *
   * So the display code and the name are matched case-insensitively too, and
   * the two objections are answered rather than avoided. The rename is a
   * non-problem: the file is read once, at import, and the id it resolves to
   * is what gets stored. The collision is real but narrow — the Redaction
   * Codes page enforces uniqueness on an exact match, so only a
   * capitalization-only pair can collide — and a collision is reported as a
   * blocking error naming the id column, never resolved by a guess.
   *
   * U5 only ever REFERENCES codes (non-goal N3), so a value that resolves to
   * nothing is a validation error rather than something to create.
   */
  readonly redactionCodes: readonly LexiconCsvRedactionCode[];
}

/**
 * `ctx.redactionCodes` in the shapes row validation actually asks for, built
 * once per file rather than per row.
 *
 * Cells are trimmed and folded with `toLowerCase`, matching what the row does
 * to them. Each bucket holds an ARRAY, not a code: the whole point of folding
 * is that it can collide, and a map that silently kept the last writer would
 * turn a collision into an arbitrary answer.
 */
type CodeBuckets = ReadonlyMap<string, readonly LexiconCsvRedactionCode[]>;

interface RedactionCodeIndex {
  readonly ids: ReadonlySet<string>;
  readonly byFoldedDisplayCode: CodeBuckets;
  readonly byFoldedName: CodeBuckets;
}

/** The code's display code — `B6`. Passed around so the resolver can report
 * an exact-case match against the same field it bucketed on. */
const displayCodeOf = (code: LexiconCsvRedactionCode): string => code.code;
/** The code's human name — `Personal Information`. */
const codeNameOf = (code: LexiconCsvRedactionCode): string => code.codeName;

function bucketBy(
  codes: readonly LexiconCsvRedactionCode[],
  textOf: (code: LexiconCsvRedactionCode) => string
): CodeBuckets {
  const buckets = new Map<string, LexiconCsvRedactionCode[]>();
  for (const code of codes) {
    const folded = textOf(code).trim().toLowerCase();
    if (!folded) {
      continue;
    }
    const bucket = buckets.get(folded);
    if (bucket) {
      bucket.push(code);
    } else {
      buckets.set(folded, [code]);
    }
  }
  return buckets;
}

function indexRedactionCodes(
  codes: readonly LexiconCsvRedactionCode[]
): RedactionCodeIndex {
  return {
    ids: new Set(codes.map((c) => c.id)),
    byFoldedDisplayCode: bucketBy(codes, displayCodeOf),
    byFoldedName: bucketBy(codes, codeNameOf),
  };
}

/**
 * Resolves a display-code or name cell to an id.
 *
 * An EXACT-CASE match wins over the folded ones. It only matters in the
 * collision case, and there it matters a lot: if an org really does have both
 * `B6` and `b6`, a file naming one of them exactly has said which, and refusing
 * it would be pedantry. Ambiguity is reported only when the file's spelling
 * matches neither exactly.
 */
function resolveCodeText(
  raw: string,
  buckets: CodeBuckets,
  textOf: (code: LexiconCsvRedactionCode) => string
): { id: string } | { message: string } {
  const candidates = buckets.get(raw.toLowerCase()) ?? [];
  if (candidates.length === 0) {
    return { message: CSV_MESSAGE_UNKNOWN_CODE };
  }
  const exact = candidates.filter((c) => textOf(c).trim() === raw);
  const resolved = exact.length === 1 ? exact[0] : candidates[0];
  if (exact.length !== 1 && candidates.length > 1) {
    return { message: csvMessageAmbiguousCode(raw) };
  }
  // Both branches above guarantee a member; the guard is for
  // `noUncheckedIndexedAccess`, not for a case that can happen.
  return resolved ? { id: resolved.id } : { message: CSV_MESSAGE_UNKNOWN_CODE };
}

/**
 * Identity used for duplicate detection (BR-29).
 *
 * On `(type, term)`, so the same text as a literal and as a regex are distinct
 * entries. Case folding follows the entry's own `caseSensitive` flag: a
 * case-insensitive entry cannot meaningfully coexist with a differently-cased
 * copy of itself, while a case-sensitive one can.
 *
 * `wholeWord` is NOT part of identity: two entries differing only in whole-word
 * matching are the same term with different matching options, and keeping both
 * is almost never what the author meant.
 */
export function lexiconEntryDuplicateKey(entry: {
  readonly term: string;
  readonly type: LexiconEntryType;
  readonly caseSensitive?: boolean;
}): string {
  const term = entry.term.trim();
  const folded =
    entry.type === 'term' && !entry.caseSensitive ? term.toLowerCase() : term;
  // U+0000 as the separator, and the invariant does NOT rest on terms being
  // free of it (nit N7). Nothing validates that: no path — this validator, the
  // composer, or a record written straight to the registry — rejects a control
  // character in a term, and this comment used to claim one did.
  //
  // What makes a cross-type collision impossible is the PREFIX. `type` is a
  // closed set of two values, neither contains a NUL and neither is a prefix of
  // the other, so `term\0A` can never equal `regex\0B` however the two terms
  // are spelled. A third type would have to preserve that property.
  //
  // (The separator was a prefix-plus-space at one point, which a term really
  // could contain. That is the change the old comment outlived, and it was
  // rewritten into a claim about validation that was never true.)
  return `${entry.type}\u0000${folded}`;
}

/**
 * Accepted `type` values, mapped to the two real types.
 *
 * `keyword` and `phrase` are accepted so a CSV written to the acceptance
 * criteria's vocabulary imports unchanged, even though both resolve to `term`
 * — see `models.ts` on why there are two types and not three.
 */
const TYPE_VOCABULARY: Readonly<Record<string, LexiconEntryType>> = {
  keyword: 'term',
  phrase: 'term',
  term: 'term',
  regex: 'regex',
  // `pattern` is what the EXPORTER writes for a regex entry
  // (`lexiconEntryKind`), and its absence here meant re-importing our own
  // export failed — and failed the WHOLE file, because validation is
  // all-or-nothing (U5-FR12), so one regex row rejected every valid row with
  // it. Round-trip is the stated requirement of both modules; it was never
  // tested across the two.
  pattern: 'regex',
};

const BOOLEAN_VOCABULARY: Readonly<Record<string, boolean>> = {
  true: true,
  false: false,
  yes: true,
  no: false,
  '1': true,
  '0': false,
};

function isCsvColumn(name: string): name is LexiconCsvColumn {
  return (LEXICON_CSV_COLUMNS as readonly string[]).includes(name);
}

/**
 * Header spellings accepted for a canonical column.
 *
 * TWO KINDS OF ALIAS, AND BOTH EARN THEIR PLACE. `redaction_code_display` and
 * `redaction_code_name` share a prefix and sort together, which is how someone
 * reading `LEXICON_CSV_COLUMNS` would expect the set to be spelled — so the
 * long form of the default column is accepted even though the exporter writes
 * the short one. `display_code` and `code_name` are the Redaction Codes page's
 * own field labels, normalized: they are what an author hand-writing a file
 * from what is on their screen would type.
 *
 * This is NOT a compatibility shim. Nothing has shipped with any of these
 * names; they are alternatives a person would plausibly reach for, and the cost
 * of not accepting one is a whole-file rejection over a header (BR-28).
 *
 * A file that spells the SAME column two ways ends with one of the two,
 * whichever Papa reads last. Not worth detecting: the columns are synonyms, so
 * either answer names the same code.
 */
const HEADER_ALIASES: Readonly<Record<string, LexiconCsvColumn>> = {
  redaction_code_display: 'redaction_code',
  display_code: 'redaction_code',
  code_name: 'redaction_code_name',
};

/** Normalizes a header cell: trims, lower-cases, accepts spaces for `_`, and
 *  resolves the aliases above to their canonical column. */
function normalizeHeader(raw: string): string {
  const normalized = raw.trim().toLowerCase().replace(/\s+/g, '_');
  return HEADER_ALIASES[normalized] ?? normalized;
}

interface RowError {
  readonly column: LexiconCsvColumn;
  readonly message: string;
}

/**
 * Validates one data row. Returns either the entry it produced or the blocking
 * errors it collected — never both, and never a partially-valid entry.
 *
 * All of a row's problems are collected rather than short-circuiting on the
 * first, because U5-FR11 highlights CELLS: an author fixing a row wants to see
 * every bad cell in it, not to rediscover them one upload at a time.
 */
function validateRow(
  row: Readonly<Record<string, string | undefined>>,
  codes: RedactionCodeIndex
): { entry: LexiconEntryInput } | { errors: readonly RowError[] } {
  const errors: RowError[] = [];

  const term = (row.term ?? '').trim();
  if (!term) {
    errors.push({ column: 'term', message: 'Term is required.' });
  } else if (term.length > ENTRY_TERM_MAX_LENGTH) {
    errors.push({
      column: 'term',
      message: `Term is too long (maximum ${ENTRY_TERM_MAX_LENGTH} characters).`,
    });
  }

  const rawType = (row.type ?? '').trim().toLowerCase();
  const type = rawType ? TYPE_VOCABULARY[rawType] : undefined;
  if (!rawType) {
    errors.push({ column: 'type', message: 'Type is required.' });
  } else if (!type) {
    errors.push({
      column: 'type',
      message: `Type must be one of: ${Object.keys(TYPE_VOCABULARY).join(
        ', '
      )}.`,
    });
  }

  const readFlag = (
    column: Extract<LexiconCsvColumn, 'case_sensitive' | 'whole_word'>
  ): boolean | undefined => {
    const raw = (row[column] ?? '').trim().toLowerCase();
    if (!raw) {
      return undefined;
    }
    const value = BOOLEAN_VOCABULARY[raw];
    if (value === undefined) {
      errors.push({ column, message: 'Must be true or false.' });
      return undefined;
    }
    return value;
  };

  // Ignored means ignored, including a value that is not a boolean at all.
  // Validating a column the entry will never read is how you get an error
  // message about a cell that does not matter — and with all-or-nothing
  // validation that message costs the whole file. Unknown `type` still reads
  // them: we cannot call a row a regex until it says so.
  const flagsApply = type !== 'regex';
  const caseSensitive = flagsApply ? readFlag('case_sensitive') : undefined;
  const wholeWord = flagsApply ? readFlag('whole_word') : undefined;

  if (type === 'regex') {
    // BR-13 — flags on a regex row are IGNORED, not rejected.
    //
    // This reverses the original reading of BR-13, deliberately and on an
    // explicit decision. It used to be a blocking cell error, on the argument
    // that accepting a flag and discarding it would mislead an author into
    // believing their pattern was case-insensitive. The cost outweighed it:
    // validation is all-or-nothing (U5-FR12), so one stray `false` left in a
    // column by a spreadsheet — which is what happens when someone fills a
    // column down — rejected the entire file, and the entry it referred to was
    // correct in every other respect. Ignoring costs an author a flag that was
    // never going to apply; rejecting costs them the upload.
    //
    // Nothing is stored either way: the flags below are attached only when
    // `type === 'term'`. The import dialog says "Ignored for regex" so the
    // discard is documented rather than silent.
    if (term) {
      const screened = validateRegexEntry(term);
      if (!screened.ok) {
        // U5-FR11 names the invalid-regex string exactly; the complexity rules
        // carry their own more specific messages, which are more actionable.
        errors.push({
          column: 'term',
          message:
            screened.ruleId === 'INVALID_SYNTAX'
              ? CSV_MESSAGE_INVALID_REGEX
              : screened.message,
        });
      }
    }
  }

  // ONE CODE, THREE WAYS TO SAY IT, AND THE MOST HUMAN ONE WINS: display code,
  // then name, then id. The first column that is filled in is read and the rest
  // are not looked at.
  //
  // THE ID GOING LAST IS THE WHOLE POINT, and it reverses the order this
  // shipped with. An export writes all three, so the only way they can disagree
  // is that a person edited one — and the one a person edits is the one they
  // can read. Under id-first, an author who opened an export, corrected the
  // `redaction_code` cell and re-imported got their edit SILENTLY DISCARDED:
  // the stale id beside it won, and the row came back stamped with the code
  // they had just changed away from. On a redaction product that is the worst
  // possible failure — wrong code, no error, no way to notice.
  //
  // It also produced errors naming `redaction_code_id`, a column the import
  // dialog deliberately does not document, for a file whose author had only
  // ever touched the column it does.
  //
  // The cost is a file that outlives a RENAME: export writes `B6`, an admin
  // changes the display code, and re-import now reports the old spelling as
  // unknown instead of quietly resolving it through the id. That is a loud,
  // fixable failure against a silent, undetectable one.
  //
  // A value that is PRESENT but unresolvable is an error, never a reason to try
  // the next column — falling through is exactly how a typo'd display code got
  // swallowed by the id sitting next to it.
  let redactionCodeId: string | undefined;
  const rawDisplayCode = (row.redaction_code ?? '').trim();
  const rawCodeName = (row.redaction_code_name ?? '').trim();
  const rawCodeId = (row.redaction_code_id ?? '').trim();
  if (rawDisplayCode) {
    const resolved = resolveCodeText(
      rawDisplayCode,
      codes.byFoldedDisplayCode,
      displayCodeOf
    );
    if ('id' in resolved) {
      redactionCodeId = resolved.id;
    } else {
      errors.push({ column: 'redaction_code', message: resolved.message });
    }
  } else if (rawCodeName) {
    const resolved = resolveCodeText(
      rawCodeName,
      codes.byFoldedName,
      codeNameOf
    );
    if ('id' in resolved) {
      redactionCodeId = resolved.id;
    } else {
      errors.push({ column: 'redaction_code_name', message: resolved.message });
    }
  } else if (rawCodeId) {
    // Exact match: an id is not a label, so nothing is folded or trimmed into
    // shape beyond the surrounding whitespace a spreadsheet adds.
    redactionCodeId = codes.ids.has(rawCodeId) ? rawCodeId : undefined;
    if (!redactionCodeId) {
      errors.push({
        column: 'redaction_code_id',
        message: CSV_MESSAGE_UNKNOWN_CODE,
      });
    }
  }

  if (errors.length > 0 || !type || !term) {
    return { errors };
  }

  return {
    entry: {
      term,
      type,
      // A row that SAID `keyword` or `phrase` stated a preference, so carry it
      // through the way the composer's dropdown does. A row typed `term` states
      // none and is left to the whitespace fallback — those two columns are not
      // interchangeable once the label is displayed back.
      ...(type === 'term' && (rawType === 'keyword' || rawType === 'phrase')
        ? { kind: rawType }
        : {}),
      ...(type === 'term' && caseSensitive !== undefined
        ? { caseSensitive }
        : {}),
      ...(type === 'term' && wholeWord !== undefined ? { wholeWord } : {}),
      ...(redactionCodeId ? { redactionCodeId } : {}),
    },
  };
}

/**
 * Validates CSV text. Pure.
 *
 * Row numbers in the returned errors are 1-based AS THE USER'S SPREADSHEET
 * NUMBERS THEM — the header is row 1, so the first data row is row 2.
 */
export function validateLexiconCsvText(
  text: string,
  ctx: LexiconCsvContext
): LexiconCsvValidationResult {
  const parsed = Papa.parse<Record<string, string | undefined>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: normalizeHeader,
    // Undo our own exporter's formula guard (review nit N1). Without this an
    // exported `-1234` re-imported as `'-1234`, matching nothing it used to —
    // a silent under-redaction the app produced on its own file. Conditional
    // by construction, so a term that really starts with an apostrophe is
    // untouched; see `csvFormulaGuard`.
    transform: unguardCsvCell,
  });

  const fields = parsed.meta.fields ?? [];
  // BR-28 — a malformed file is rejected wholesale, never partially
  // interpreted. `term` and `type` are required; the other three columns are
  // optional, and unrecognized columns are ignored rather than rejected so an
  // export carrying extra bookkeeping columns still imports.
  const missing = (['term', 'type'] as const).filter(
    (c) => !fields.some((f) => isCsvColumn(f) && f === c)
  );
  if (missing.length > 0) {
    return {
      ok: false,
      fileError: {
        message: `CSV is missing required column(s): ${missing.join(
          ', '
        )}. Expected columns: ${LEXICON_CSV_COLUMNS.join(', ')}.`,
      },
      cellErrors: [],
      skippedExistingCount: 0,
    };
  }

  const rows = parsed.data;
  if (rows.length === 0) {
    return {
      ok: false,
      fileError: { message: 'CSV contains no data rows.' },
      cellErrors: [],
      skippedExistingCount: 0,
    };
  }

  // BR-16 — an absolute-ceiling check BEFORE the row loop. No file with more
  // rows than the per-lexicon maximum can import, whatever the rows say, so
  // validating each of them first — regex screening included — is work whose
  // only possible outcome is this same rejection. The headroom check below
  // still runs after the loop, and has to: it is measured against what would
  // ACTUALLY be added, which is not known until the skips are counted.
  if (rows.length > LEXICON_MAX_ENTRIES) {
    return {
      ok: false,
      fileError: {
        message: `This file has ${rows.length} rows, exceeding the limit of ${LEXICON_MAX_ENTRIES} entries per lexicon.`,
      },
      cellErrors: [],
      skippedExistingCount: 0,
    };
  }

  const codeIndex = indexRedactionCodes(ctx.redactionCodes);
  const cellErrors: LexiconCsvCellError[] = [];
  const accepted: LexiconEntryInput[] = [];
  let skippedExistingCount = 0;

  // Duplicate key -> the spreadsheet rows that produced it. Collected across
  // the whole file first, because BR-22 highlights EVERY occurrence of a
  // duplicated term, not just the second one.
  const rowsByKey = new Map<string, number[]>();

  rows.forEach((row, index) => {
    const spreadsheetRow = index + 2;
    const result = validateRow(row, codeIndex);
    if ('errors' in result) {
      for (const err of result.errors) {
        cellErrors.push({
          row: spreadsheetRow,
          column: err.column,
          message: err.message,
          blocking: true,
        });
      }
      return;
    }
    const key = lexiconEntryDuplicateKey(result.entry);
    const seen = rowsByKey.get(key);
    if (seen) {
      seen.push(spreadsheetRow);
    } else {
      rowsByKey.set(key, [spreadsheetRow]);
    }
    if (ctx.existingKeys.has(key)) {
      // BR-23 — already in the lexicon: skip, count, do not block.
      skippedExistingCount += 1;
      return;
    }
    accepted.push(result.entry);
  });

  // BR-22 — in-file duplicates are blocking, and every occurrence is flagged.
  const duplicateKeys = new Set<string>();
  for (const [key, rowNumbers] of rowsByKey) {
    if (rowNumbers.length > 1) {
      duplicateKeys.add(key);
      for (const rowNumber of rowNumbers) {
        cellErrors.push({
          row: rowNumber,
          column: 'term',
          message: CSV_MESSAGE_DUPLICATE,
          blocking: true,
        });
      }
    }
  }

  if (cellErrors.some((e) => e.blocking)) {
    return { ok: false, cellErrors, skippedExistingCount };
  }

  // De-duplicate the accepted set against itself. Unreachable while every
  // in-file duplicate is blocking, but it keeps the postcondition ("no two
  // accepted entries share a duplicate key") true independently of that rule,
  // so a future relaxation of BR-22 cannot silently produce duplicate entries.
  const entries = accepted.filter(
    (e) => !duplicateKeys.has(lexiconEntryDuplicateKey(e))
  );

  // BR-16 / BR-27 — headroom is checked against what would actually be added,
  // after skips, so re-uploading a superset of an already-imported list is not
  // rejected on account of terms that would not be added.
  if (entries.length > ctx.remainingHeadroom) {
    return {
      ok: false,
      fileError: {
        message: `This upload would add ${entries.length} entries, exceeding the limit of ${LEXICON_MAX_ENTRIES} per lexicon (${ctx.remainingHeadroom} remaining).`,
      },
      cellErrors: [],
      skippedExistingCount,
    };
  }

  return { ok: true, entries, skippedExistingCount };
}

/**
 * Validates an uploaded CSV file.
 *
 * The size gate runs BEFORE any parsing (BR-27, SECURITY-05): a 40 MB file
 * should be refused, not streamed through a parser first.
 */
export async function validateLexiconCsv(
  file: File,
  ctx: LexiconCsvContext
): Promise<LexiconCsvValidationResult> {
  if (file.size > CSV_MAX_BYTES) {
    return {
      ok: false,
      fileError: {
        message: `File is too large (maximum ${Math.floor(
          CSV_MAX_BYTES / 1024
        )} KB).`,
      },
      cellErrors: [],
      skippedExistingCount: 0,
    };
  }
  let text: string;
  try {
    text = await file.text();
  } catch {
    return {
      ok: false,
      fileError: { message: 'Could not read the file.' },
      cellErrors: [],
      skippedExistingCount: 0,
    };
  }
  return validateLexiconCsvText(text, ctx);
}
